/**
 * NOTIFY USER — the client MIRROR of `server/helpers/notifyReasons.js`.
 *
 * Product owner, 2026-08-11: *"Maybe the action should just be **Notify User**
 * with a selection box of options like out of stock, drastic price difference,
 * longer than expected lead times etc so the buyer/supervisor can just tick a
 * box and then leave a note if they wish"*.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🚨 A CONVENIENCE, NEVER A GATE
 * ─────────────────────────────────────────────────────────────────────────────
 * This exists so the picker can render labels and enforce the note cap without a
 * round trip. The SERVER re-validates every write — unknown keys are dropped, an
 * over-long note is refused 400, and `other` without a note is refused 400 — so
 * nothing here decides anything. Same standing as `utils/poNumber.js` and
 * `utils/supplierOptions.js`, and held in step by the same device: a shared case
 * table run by both sides (`constants/notifyReasons.test.js` here, §3 of
 * `server/tests/api/order-item-notify.test.js` there).
 *
 * 🔑 **THE CLIENT NEVER COMPOSES THE MESSAGE, AND MUST NOT LEARN HOW.** The
 * notification text is built entirely on the server from stored values and the
 * caller's identity, because it lands in somebody ELSE's notification bell — a
 * client-composed message would be an arbitrary-text injection into another
 * user's inbox, signed with the caller's name and delivered by the app. So there
 * is deliberately no `composeNotifyMessage` mirror here, and adding one would be
 * the first step toward the client sending it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ADDING A REASON
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE entry here and ONE in the server file. The picker maps over the array, so
 * nothing else changes — that is the brief's standing requirement that a fourth
 * reason must not need new plumbing, and it is a property of the SHAPE rather
 * than of where the list lives.
 *
 * 🪤 The `key` is a stable machine token and is what gets STORED
 * (`order_items.notify_reasons`, a CSV). The `label` is display only. Never
 * store a label: reword a checkbox and every historical row becomes a lie about
 * what the buyer ticked.
 */

/** Ordered as the picker renders them. Keep `other` last — see the server file. */
export const NOTIFY_REASONS = Object.freeze([
  Object.freeze({ key: 'out_of_stock', label: 'Out of stock' }),
  Object.freeze({ key: 'price_difference', label: 'Big price difference' }),
  Object.freeze({ key: 'long_lead_time', label: 'Longer lead time than expected' }),
  Object.freeze({ key: 'other', label: 'Other' }),
]);

export const NOTIFY_REASON_KEYS = Object.freeze(NOTIFY_REASONS.map((r) => r.key));

/** The key meaning "not on the list", which makes the note REQUIRED. */
export const NOTIFY_OTHER_KEY = 'other';

/**
 * 📏 Measured against the surface that renders it — see the server file for the
 * full working. The short version: the bell applies no truncation and no
 * line-clamp, the desktop tray is 352px of 14px text (~50 characters a line),
 * and the longest message in 6,442 existing rows is 123 characters.
 *
 * 🔑 The client sets `maxLength` and shows a counter so the server's 400 is
 * unreachable through the UI. That is the ONLY reason the server refuses rather
 * than truncating without anybody noticing.
 */
export const NOTIFY_NOTE_MAX_LENGTH = 140;

/** Stored CSV → key list. Unknown keys are dropped, so an old row cannot render a ghost. */
export function reasonsFromStored(stored) {
  if (typeof stored !== 'string' || stored.trim() === '') return [];
  const wanted = new Set(stored.split(',').map((s) => s.trim()));
  return NOTIFY_REASON_KEYS.filter((key) => wanted.has(key));
}

/** Is this line flagged? THE predicate — never test `notified_at` or `notified_by_user_id`. */
export function isLineFlagged(item) {
  return reasonsFromStored(item && item.notify_reasons).length > 0;
}

/** Human labels for a key list, in canonical order. */
export function reasonLabels(keys) {
  const wanted = new Set(Array.isArray(keys) ? keys : []);
  return NOTIFY_REASONS.filter((r) => wanted.has(r.key)).map((r) => r.label);
}

/**
 * The short text a flagged row shows.
 *
 * 🔑 UNLIKE THE NOTIFICATION, THIS DOES PRINT "Other" — and the difference is
 * deliberate rather than an inconsistency. In the notification the free text is
 * right there in the same sentence, so "Other" would be a word that says nothing;
 * on the ROW the note is not shown (it is in the tooltip), so dropping the last
 * reason would leave a line flagged for no visible cause.
 */
export function flagSummary(item) {
  const labels = reasonLabels(reasonsFromStored(item && item.notify_reasons));
  return labels.join(' · ');
}
